/**
 * Every failure the user can hit gets a code and a Japanese message. Nothing
 * is allowed to end in a black screen or a console-only error.
 */
export type AppErrorCode =
  | 'ENGINE_LOAD_FAILED'
  | 'ENGINE_SHAPE_MISMATCH'
  | 'CANVAS_MISSING'
  | 'MANIFEST_INVALID'
  | 'MODEL_FETCH_FAILED'
  | 'MODEL_PARSE_FAILED'
  | 'UNSUPPORTED_DEVICE'
  | 'CAPTURE_FAILED'

const MESSAGES_JA: Record<AppErrorCode, string> = {
  ENGINE_LOAD_FAILED: '通信が不安定みたい。電波のいい場所で読み込み直してね。',
  ENGINE_SHAPE_MISMATCH: 'ARエンジンを起動できませんでした。読み込み直してね。',
  CANVAS_MISSING: '画面の初期化に失敗しました。読み込み直してね。',
  MANIFEST_INVALID: '設定ファイルに問題があります。',
  MODEL_FETCH_FAILED: 'コンテンツを読み込めませんでした。もう一度試してね。',
  MODEL_PARSE_FAILED: 'コンテンツを読み込めませんでした。もう一度試してね。',
  UNSUPPORTED_DEVICE: 'この端末・ブラウザではARを表示できません。スマートフォンのSafariまたはChromeで開いてね。',
  // Shown in `#hint`, which is `white-space: nowrap` - measured at 323 px
  // against the narrowest supported viewport (375 px). Keep it under ~20 chars.
  CAPTURE_FAILED: '写真を撮れなかったよ。もう一度試してね',
}

export interface AppErrorOptions {
  readonly cause?: unknown
  /**
   * Overrides the default Japanese message. Use when a specific, actionable
   * message beats the generic one - notably manifest validation, where naming
   * the offending key is the difference between a fixable error and a mystery.
   */
  readonly userMessageJa?: string
}

export class AppError extends Error {
  readonly code: AppErrorCode
  /** Safe to show to an end user. Never contains a stack or a URL. */
  readonly userMessageJa: string

  constructor(code: AppErrorCode, detail?: string, options?: AppErrorOptions) {
    super(detail ? `${code}: ${detail}` : code, {cause: options?.cause})
    this.name = 'AppError'
    this.code = code
    this.userMessageJa = options?.userMessageJa ?? MESSAGES_JA[code]
  }
}

/** Narrows an unknown thrown value to something loggable. */
export const describeError = (error: unknown): string =>
  error instanceof Error ? `${error.name}: ${error.message}` : String(error)
